/**
 * Ocean drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawings`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const oceanDrawingsPack: ToilePack = {
    slug: 'ocean-drawings',
    category: 'illustration',
    name: 'Ocean drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'wave', label: 'Curling wave', subject: 'a large wave curling over at its crest, spray lifting off the top and foam lines across its face'},
        {key: 'lighthouse', label: 'Lighthouse', subject: 'a lighthouse on a rocky point with waves breaking below and gulls wheeling overhead'},
        {key: 'turtle', label: 'Sea turtle', subject: 'a sea turtle gliding over a coral reef, small fish around it and light rippling down from the surface'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil', style: 'graphite pencil drawing, soft layered shading from sharp 2H detail to deep 6B darks, visible paper tooth', palette: 'graphite gray only', framing: 'on plain white paper, the whole sheet used, a thin white margin around the drawing'},
        {key: 'ink', label: 'Pen and ink', style: 'pen and ink drawing, confident contour lines, fine crosshatching and stippling for tone', palette: 'black ink only', framing: 'on clean white paper, centered, a white margin all round'},
        {key: 'charcoal', label: 'Charcoal', style: 'charcoal drawing, smudged tonal masses with crisp accents and lifted highlights', palette: 'black charcoal on white', framing: 'on white drawing paper, the subject filling the sheet, a white edge left clear'},
    ],
    directions: ['a whale', 'a storm', 'sunset'],
    directionHint: 'try: a whale',
    seo: {
        title: 'Ocean Drawings: Curling Wave, Lighthouse, Sea Turtle',
        metaDescription: 'Nine ocean drawings: a curling wave, a lighthouse on a rocky point and a sea turtle over a reef, each in pencil, pen and ink, or charcoal.',
        h1: 'Ocean drawings: a curling wave, a lighthouse, a sea turtle',
        tagline: 'Three ocean scenes, each drawn in graphite pencil, pen and ink, and charcoal.',
        intro: 'Three ocean subjects: a large wave curling over with spray from its crest, a lighthouse on a rocky point, and a sea turtle gliding over a coral reef. Each is drawn in pencil, pen and ink, and charcoal, so the movement of water and the shape of the animal compare across the media. Type a different sea subject or a twist, such as a whale or a storm, and the same media carry it.',
        useCases: [
            'a sketchbook study of moving water',
            'a framed print for a beach house',
            'a reference for waves and spray',
            'a sea life page for a nature journal',
        ],
        faq: [
            {q: 'What is in this pack?', a: 'Nine drawings of three ocean subjects: a curling wave, a lighthouse on a rocky point, and a sea turtle over a reef. Each appears in three media.'},
            {q: 'Can I ask for a different sea scene?', a: 'Yes. Type a subject or twist, such as a whale, a storm or sunset. A typed direction is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Pencil uses graphite shading on plain white paper. Pen and ink uses contour lines, crosshatching and stippling. Charcoal uses smudged tone with crisp accents.'},
            {q: 'Is each drawing wide or tall?', a: 'Wide. The ocean drawings are wider than they are tall, which suits a long line of surf.'},
        ],
    },
}
